import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, Calendar, ChevronDown, X, Users } from 'lucide-react';

const FilterBar = ({ 
  activeCategory, 
  setActiveCategory, 
  timeRange, 
  setTimeRange, 
  categories,
  customerType,
  setCustomerType,
  customerTypeCounts = { managed: 0, unmanaged: 0, total: 0 }
}) => {
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showTimeMenu, setShowTimeMenu] = useState(false);
  const [showCustomerTypeMenu, setShowCustomerTypeMenu] = useState(false);
  
  const timeRangeOptions = [
    { value: '1m', label: 'Last month' },
    { value: '3m', label: 'Last 3 months' },
    { value: '6m', label: 'Last 6 months' },
    { value: '1y', label: 'Last year' },
    { value: 'all', label: 'All time' }
  ];

  const customerTypeOptions = [
    { value: 'all', label: `All customers (${customerTypeCounts.total})` },
    { value: 'managed', label: `Managed customers (${customerTypeCounts.managed})` },
    { value: 'unmanaged', label: `Unmanaged customers (${customerTypeCounts.unmanaged})` }
  ];
  
  const toggleCategoryMenu = () => {
    setShowCategoryMenu(!showCategoryMenu);
    if (showTimeMenu) setShowTimeMenu(false);
    if (showCustomerTypeMenu) setShowCustomerTypeMenu(false);
  };
  
  const toggleTimeMenu = () => {
    setShowTimeMenu(!showTimeMenu);
    if (showCategoryMenu) setShowCategoryMenu(false);
    if (showCustomerTypeMenu) setShowCustomerTypeMenu(false);
  };

  const toggleCustomerTypeMenu = () => {
    setShowCustomerTypeMenu(!showCustomerTypeMenu);
    if (showCategoryMenu) setShowCategoryMenu(false);
    if (showTimeMenu) setShowTimeMenu(false);
  };
  
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setShowCategoryMenu(false);
  };
  
  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    setShowTimeMenu(false);
  };

  const handleCustomerTypeChange = (type) => {
    setCustomerType(type);
    setShowCustomerTypeMenu(false);
  };
  
  const resetFilters = () => {
    setActiveCategory('all');
    setTimeRange('all');
    setCustomerType('all');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <button 
              className={`flex items-center space-x-2 ${showCustomerTypeMenu ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'} border rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors`}
              onClick={toggleCustomerTypeMenu}
            >
              <Users className="w-4 h-4 text-gray-500" />
              <span>{customerTypeOptions.find(option => option.value === customerType)?.label || 'Customer type'}</span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            
            {showCustomerTypeMenu && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute z-10 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
              >
                <div className="py-1" role="menu" aria-orientation="vertical">
                  {customerTypeOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`block px-4 py-2 text-sm w-full text-left ${customerType === option.value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleCustomerTypeChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <div className="relative">
            <button 
              className={`flex items-center space-x-2 ${showTimeMenu ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'} border rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors`}
              onClick={toggleTimeMenu}
            >
              <Calendar className="w-4 h-4 text-gray-500" />
              <span>{timeRangeOptions.find(option => option.value === timeRange)?.label || 'Time range'}</span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            
            {showTimeMenu && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute z-10 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
              >
                <div className="py-1" role="menu" aria-orientation="vertical">
                  {timeRangeOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`block px-4 py-2 text-sm w-full text-left ${timeRange === option.value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleTimeRangeChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
          
          <div className="relative">
            {/* <button 
              className={`flex items-center space-x-2 ${showCategoryMenu ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200'} border rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors`}
              onClick={toggleCategoryMenu}
            >
              <span>{activeCategory === 'all' ? 'All categories' : activeCategory.replace('_', ' ')}</span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button> */}
            
            {showCategoryMenu && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute z-10 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
              >
                <div className="py-1 max-h-60 overflow-y-auto" role="menu" aria-orientation="vertical">
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${activeCategory === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => handleCategoryChange('all')}
                  >
                    All categories
                  </button>
                  
                  {categories.map((category) => (
                    <button
                      key={category}
                      className={`block px-4 py-2 text-sm w-full text-left ${activeCategory === category ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleCategoryChange(category)}
                    >
                      {category.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
          
          <button 
            onClick={resetFilters}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm transition-colors"
          >
            Reset filters
          </button>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2 mt-4">
        {customerType !== 'all' && (
          <div className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-medium flex items-center">
            Customer: {customerTypeOptions.find(option => option.value === customerType)?.label.replace(' customers', '')}
            <button 
              className="ml-1.5 text-indigo-600 hover:text-indigo-800 focus:outline-none"
              onClick={() => setCustomerType('all')}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {activeCategory !== 'all' && (
          <div className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-medium flex items-center">
            Category: {activeCategory.replace('_', ' ')}
            <button 
              className="ml-1.5 text-indigo-600 hover:text-indigo-800 focus:outline-none"
              onClick={() => setActiveCategory('all')}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        
        {timeRange !== 'all' && (
          <div className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-medium flex items-center">
            Time: {timeRangeOptions.find(option => option.value === timeRange)?.label}
            <button 
              className="ml-1.5 text-indigo-600 hover:text-indigo-800 focus:outline-none"
              onClick={() => setTimeRange('all')}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default FilterBar;